import {
  VStack,
  HStack,
  Text,
  Box,
  IconButton,
  Link,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useMemo, useCallback } from 'react';
import ChevronLeftSharpIcon from '@gamma/ui/icons/ChevronLeftSharpIcon';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import FootprintSharpIcon from '@gamma/icons/FootprintSharpIcon';
import DatabaseSharpIcon from '@gamma/ui/icons/DatabaseSharpIcon';
import ChevronRightSharpIcon from '@gamma/ui/icons/ChevronRightSharpIcon';
import { useEntityAgenticTriageReportContext } from '../../../context';
import { useEntitiesContext } from '../../../context';
import type { AssessmentStep } from '@investigator/shared/generated/graphql';
import { getTruncatedText } from '../../../utils/helpers';
import { i18n } from '@investigator/shared/localization';
import { ModalView } from '../../../types';

const { title, subtitle, findings } = i18n.pages.entities.playbookModal;

const ESTIMATED_CHARS_PER_LINE = 60;
const MAX_CHARS_FOR_2_LINES = ESTIMATED_CHARS_PER_LINE * 2;
const MAX_CHARS_FOR_TEXT = MAX_CHARS_FOR_2_LINES - 10;
const MIN_CHARS_FOR_TRUNCATION = 180;

const iconButtonStyles = {
  w: '24px',
  h: '24px',
  minW: '24px',
  color: 'text.primary',
  bg: 'transparent',
  border: 'none',
  borderRadius: 'base',
  _hover: {
    bg: 'transparent',
    border: 'none',
  },
  _focus: {
    boxShadow: 'none',
    outline: 'none',
  },
  _focusVisible: {
    boxShadow: 'none',
    outline: 'none',
  },
} as const;

const stepNameTypography = {
  fontWeight: 600,
  lineHeight: '20px',
  letterSpacing: '0.16px',
  color: 'text.secondary',
} as const;

const summaryTypography = {
  color: 'text.primary',
  fontWeight: 500,
  lineHeight: '20px',
  letterSpacing: '0.16px',
} as const;

interface PlaybookStepProps {
  step: AssessmentStep;
  isLast: boolean;
}

const PlaybookStep = ({ step, isLast }: PlaybookStepProps) => {
  const { isOpen, onToggle } = useDisclosure();
  const hasMore = (step.summary?.length ?? 0) > MIN_CHARS_FOR_TRUNCATION;

  const displayText = useMemo(
    () =>
      !isOpen && hasMore && step.summary
        ? getTruncatedText(step.summary, MAX_CHARS_FOR_TEXT)
        : (step.summary ?? ''),
    [isOpen, hasMore, step.summary],
  );

  return (
    <VStack align="stretch" spacing={0}>
      <HStack align="flex-start" spacing={3}>
        <VStack align="center" spacing={0} flexShrink={0}>
          {/* Green dot */}
          <Box
            w="20px"
            h="20px"
            borderRadius="24px"
            border="4px solid"
            borderColor="green.700"
            bg="green.400"
            mt="2px"
          />
          {/* Vertical line connecting to next step */}
          {!isLast && <Box w="2px" h="68px" bg="gray.700" mt={2} />}
        </VStack>
        <VStack align="stretch" spacing={1} flex={1}>
          <Text {...stepNameTypography}>{step.step_name ?? ''}</Text>
          <Text
            {...summaryTypography}
            sx={{
              ...(!isOpen &&
                hasMore && {
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }),
            }}
          >
            {displayText}
            {hasMore && (
              <>
                {' '}
                <Link
                  onClick={onToggle}
                  {...summaryTypography}
                  color="text.link.action"
                  textDecoration="underline"
                  cursor="pointer"
                  display="inline"
                  whiteSpace="nowrap"
                >
                  {isOpen ? 'less' : 'more'}
                </Link>
              </>
            )}
          </Text>
        </VStack>
      </HStack>
    </VStack>
  );
};

interface PlaybookHeaderProps {
  onBack: () => void;
  onClose: () => void;
}

const PlaybookHeader = ({ onBack, onClose }: PlaybookHeaderProps) => (
  <ModalHeader pt={5} borderBottom="1px solid" borderColor="border.layer.1">
    <HStack justify="space-between" align="center">
      <HStack spacing={3}>
        <IconButton
          icon={<ChevronLeftSharpIcon />}
          aria-label="Back"
          variant="ghost"
          {...iconButtonStyles}
          onClick={onBack}
        />
        <Text fontWeight={400}>{title}</Text>
      </HStack>
      <IconButton
        icon={<CloseSharpIcon />}
        aria-label="Close"
        variant="ghost"
        {...iconButtonStyles}
        onClick={onClose}
      />
    </HStack>
  </ModalHeader>
);

export const PlaybookView: React.FC = () => {
  const {
    analysisModal: { modalNavigation, onClose },
  } = useEntitiesContext();
  const { report, isLoading } = useEntityAgenticTriageReportContext();

  const playbookSteps = useMemo(
    () =>
      report?.playbook?.assessment_summary?.filter(
        (step): step is AssessmentStep => step !== null,
      ) || [],
    [report?.playbook?.assessment_summary],
  );

  const finalStep = useMemo(
    () => playbookSteps[playbookSteps.length - 1],
    [playbookSteps],
  );

  const otherSteps = useMemo(() => playbookSteps.slice(0, -1), [playbookSteps]);

  const findingsCount = useMemo(
    () =>
      report?.key_findings_info?.key_findings?.filter(
        (finding) => finding !== null,
      ).length || 0,
    [report?.key_findings_info?.key_findings],
  );

  const handleViewFindings = useCallback(() => {
    modalNavigation.navigateTo(ModalView.KeyFindings);
  }, [modalNavigation]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleViewFindings();
      }
    },
    [handleViewFindings],
  );

  if (isLoading) {
    return (
      <>
        <PlaybookHeader
          onBack={modalNavigation.goBackToMain}
          onClose={onClose}
        />
        <ModalBody overflowY="auto">
          <Text>Loading...</Text>
        </ModalBody>
        <ModalFooter borderTop="1px solid" borderColor="border.layer.1">
          <Button variant="solid" colorScheme="gray" onClick={onClose}>
            {i18n.buttons.close}
          </Button>
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      <PlaybookHeader onBack={modalNavigation.goBackToMain} onClose={onClose} />
      <ModalBody overflowY="auto">
        <VStack align="stretch" spacing={4}>
          <HStack spacing={2} align="center">
            <FootprintSharpIcon w="16px" h="16px" color="text.primary" />
            <Text {...summaryTypography} fontWeight={600} color="text.primary">
              {subtitle}
            </Text>
          </HStack>

          {playbookSteps.length === 0 ? (
            <Text color="text.secondary" fontStyle="italic">
              No investigative steps available for this entity.
            </Text>
          ) : (
            <>
              {otherSteps.map((step: AssessmentStep, index: number) => (
                <PlaybookStep key={index} step={step} isLast={false} />
              ))}

              {finalStep && (
                <VStack align="stretch" spacing={2}>
                  <PlaybookStep step={finalStep} isLast={true} />
                  {findingsCount > 0 && (
                    <HStack
                      spacing={2}
                      align="center"
                      ml={8}
                      role="button"
                      tabIndex={0}
                      cursor="pointer"
                      onClick={handleViewFindings}
                      onKeyDown={handleKeyDown}
                      _hover={{
                        opacity: 0.8,
                      }}
                      _focus={{
                        outline: '2px solid',
                        outlineColor: 'blue.500',
                        outlineOffset: '2px',
                      }}
                    >
                      <DatabaseSharpIcon
                        w="16px"
                        h="16px"
                        color="text.primary"
                      />
                      <Text {...summaryTypography} color="text.primary">
                        {findingsCount} {findings}
                      </Text>
                      <ChevronRightSharpIcon
                        w="16px"
                        h="16px"
                        color="text.primary"
                      />
                    </HStack>
                  )}
                </VStack>
              )}
            </>
          )}
        </VStack>
      </ModalBody>
      <ModalFooter borderTop="1px solid" borderColor="border.layer.1">
        <Button variant="solid" colorScheme="gray" onClick={onClose}>
          {i18n.buttons.close}
        </Button>
      </ModalFooter>
    </>
  );
};
